/**
 * Root component and route table for the Venture Readiness Portal.
 *
 * Wraps the app in AuthProvider + DataRoomProvider, then defines all routes.
 * Public routes (login, apply, force-change-password) render bare; every other
 * route is nested under ProtectedRoute (auth gate) and Layout (shared navbar).
 * The same component handles multiple roles — role-specific access is enforced
 * by each page/endpoint, not by the route table here. The catch-all "*" sends
 * users to their role's home via RoleRedirect.
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { DataRoomProvider } from './context/DataRoomContext';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

import Login from './pages/Login';
import Apply from './pages/Apply';
import ForceChangePassword from './pages/ForceChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FounderDashboard from './pages/FounderDashboard';
import EIRDashboard from './pages/EIRDashboard';
import EIRConnections from './pages/EIRConnections';
import EIRDataRoom from './pages/EIRDataRoom';
import EIRFiles from './pages/EIRFiles';
import EIRFundraising from './pages/EIRFundraising';
import Files from './pages/Files';
import CEGDashboard from './pages/CEGDashboard';
import Account from './pages/Account';
import InvestorCRM from './pages/InvestorCRM';
import EIRCRM from './pages/EIRCRM';
import AdminOverview from './pages/AdminOverview';
import AdminAccounts from './pages/AdminAccounts';
import AdminApplications from './pages/AdminApplications';
import AdminConnections from './pages/AdminConnections';
import DataRoom from './pages/DataRoom';
import FundraisingPlan from './pages/FundraisingPlan';
// CEG micro-grant funding is parked — routes below are commented out, so these
// imports are unused. Kept alongside them for a one-line revert.
// import FundingApplication from './pages/FundingApplication';
// import AdminFundingApplications from './pages/AdminFundingApplications';

// Redirects to the correct home route based on role; falls back to /login when
// the user or role is unknown (e.g. session not yet resolved).
function RoleRedirect() {
  const { user } = useAuth();
  const home = { founder: '/dashboard', eir: '/eir', ceg: '/ceg', admin: '/admin' };
  return <Navigate to={home[user?.role] || '/login'} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <DataRoomProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes — no auth, no navbar */}
          <Route path="/login" element={<Login />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/force-change-password" element={<ForceChangePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected routes — auth required, shared Layout (navbar) */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>

              {/* Founder */}
              <Route path="/dashboard"             element={<FounderDashboard />} />
              <Route path="/data-room"             element={<DataRoom />} />
              <Route path="/data-room/:categoryId" element={<DataRoom />} />
              <Route path="/files"                 element={<Files />} />
              <Route path="/fundraising"           element={<FundraisingPlan />} />
              <Route path="/crm"                   element={<InvestorCRM />} />
              {/* <Route path="/funding"               element={<FundingApplication />} /> */}

              {/* EIR */}
              <Route path="/eir"             element={<EIRDashboard />} />
              <Route path="/eir/connections" element={<EIRConnections />} />
              <Route path="/eir/data-room"   element={<EIRDataRoom />} />
              <Route path="/eir/files"       element={<EIRFiles />} />
              <Route path="/eir/fundraising" element={<EIRFundraising />} />
              <Route path="/eir/crm"         element={<EIRCRM />} />

              {/* CEG */}
              <Route path="/ceg" element={<CEGDashboard />} />

              {/* Admin */}
              <Route path="/admin"              element={<AdminOverview />} />
              <Route path="/admin/accounts"     element={<AdminAccounts />} />
              <Route path="/admin/applications"        element={<AdminApplications />} />
              <Route path="/admin/connections"  element={<AdminConnections />} />
              {/* <Route path="/admin/funding-applications" element={<AdminFundingApplications />} /> */}

              {/* Account — all roles */}
              <Route path="/account" element={<Account />} />

              {/* Default: redirect to role-appropriate home */}
              <Route path="*" element={<RoleRedirect />} />

            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      </DataRoomProvider>
    </AuthProvider>
  );
}
